import React, { useCallback, useEffect, useRef, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';
import { createInviteDialogEvent } from '../../../../analytics/AnalyticsEvents';
import { sendAnalytics } from '../../../../analytics/functions';
import { getRoomName } from '../../../../base/conference/functions';
import { getInviteURL } from '../../../../base/connection/functions';
import { hideDialog } from '../../../../base/dialog/actions';
import { translate } from '../../../../base/i18n/functions';
import Icon from '../../../../base/icons/components/Icon';
import { IconCloseLarge } from '../../../../base/icons/svg';
import { copyText } from '../../../../base/util/copyText.web';
import { getDecodedURI } from '../../../../base/util/uri';
import { showSuccessNotification } from '../../../../notifications/actions';
import { NOTIFICATION_TIMEOUT_TYPE } from '../../../../notifications/constants';
import { INVITE_DIALOG_HEADER_HEIGHT_PX, INVITE_DIALOG_WIDTH_PX, INVITE_PRIMARY_BUTTON_COLOR, INVITE_PRIMARY_BUTTON_HOVER_COLOR } from '../../../constants';
import { formatMeetingIdForTitle, getInviteText } from '../../../functions';
import logger from '../../../logger';
const useStyles = makeStyles()(theme => {
    return {
        overlay: {
            position: 'fixed',
            inset: 0,
            zIndex: 301,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            animation: `${keyframes `
                0% { opacity: 0; }
                100% { opacity: 1; }
            `} 0.15s ease-out`
        },
        panel: {
            width: `${INVITE_DIALOG_WIDTH_PX}px`,
            maxWidth: 'calc(100vw - 32px)',
            backgroundColor: theme.palette.dialogBackground,
            border: `1px solid ${theme.palette.dialogBorder}`,
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.32)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            position: 'relative',
            userSelect: 'none'
        },
        header: {
            height: `${INVITE_DIALOG_HEADER_HEIGHT_PX}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            cursor: 'move',
            flexShrink: 0
        },
        title: {
            margin: 0,
            fontSize: '14px',
            fontWeight: 500,
            color: theme.palette.dialogText,
            lineHeight: 1.4
        },
        closeBtn: {
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '28px',
            height: '28px',
            border: 'none',
            background: 'transparent',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.icon02,
            padding: 0,
            '&:hover': {
                backgroundColor: theme.palette.action03Hover,
                color: theme.palette.icon01
            },
            '& svg': {
                fill: 'currentColor'
            }
        },
        body: {
            padding: '8px 24px 4px',
            minHeight: '160px'
        },
        inviteText: {
            margin: 0,
            padding: 0,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            fontSize: '13px',
            lineHeight: 1.7,
            color: theme.palette.dialogText,
            fontFamily: 'inherit',
            userSelect: 'text'
        },
        footer: {
            padding: '16px 20px 24px',
            display: 'flex',
            justifyContent: 'center'
        },
        copyBtn: {
            minWidth: '200px',
            height: '40px',
            padding: '0 28px',
            border: 'none',
            borderRadius: '6px',
            backgroundColor: INVITE_PRIMARY_BUTTON_COLOR,
            color: '#fff',
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
            transition: 'background-color 0.15s ease',
            '&:hover': {
                backgroundColor: INVITE_PRIMARY_BUTTON_HOVER_COLOR
            },
            '&:active': {
                backgroundColor: '#0052cc'
            }
        },
        // 供 E2E / 无障碍读取的隐藏会议链接与会议号
        srOnly: {
            position: 'absolute',
            width: '1px',
            height: '1px',
            padding: 0,
            margin: '-1px',
            overflow: 'hidden',
            clip: 'rect(0, 0, 0, 0)',
            whiteSpace: 'nowrap',
            border: 0
        }
    };
});
/**
 * 会议邀请弹窗：可拖动；展示邀请文案并支持一键复制。
 *
 * @returns {React$Element<any>}
 */
function AddPeopleDialog({ _invitationText, _inviteUrl, _meetingId, t }) {
    const { classes } = useStyles();
    const dispatch = useDispatch();
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const dragRef = useRef({
        dragging: false,
        originX: 0,
        originY: 0,
        startX: 0,
        startY: 0
    });
    useEffect(() => {
        sendAnalytics(createInviteDialogEvent('opened', 'dialog'));
        logger.info('[邀请弹窗] 已打开', {
            meetingId: _meetingId,
            hasInviteUrl: Boolean(_inviteUrl)
        });
        return () => {
            sendAnalytics(createInviteDialogEvent('closed', 'dialog'));
            logger.info('[邀请弹窗] 已关闭');
        };
    }, [_inviteUrl, _meetingId]);
    const onClose = useCallback(() => {
        dispatch(hideDialog());
    }, [dispatch]);
    const onCopyAll = useCallback(async () => {
        const ok = await copyText(_invitationText);
        if (ok) {
            logger.info('[邀请弹窗] 复制全部信息成功', {
                meetingId: _meetingId,
                textLength: _invitationText.length
            });
            dispatch(showSuccessNotification({
                titleKey: 'dialog.copied'
            }, NOTIFICATION_TIMEOUT_TYPE.SHORT));
        }
        else {
            logger.error('[邀请弹窗] 复制全部信息失败', { meetingId: _meetingId });
        }
    }, [_invitationText, _meetingId, dispatch]);
    const onHeaderMouseDown = useCallback((e) => {
        // 关闭按钮区域不触发拖动
        if (e.target.closest('button')) {
            return;
        }
        e.preventDefault();
        dragRef.current = {
            dragging: true,
            originX: offset.x,
            originY: offset.y,
            startX: e.clientX,
            startY: e.clientY
        };
    }, [offset.x, offset.y]);
    useEffect(() => {
        const onMove = (e) => {
            if (!dragRef.current.dragging) {
                return;
            }
            setOffset({
                x: dragRef.current.originX + (e.clientX - dragRef.current.startX),
                y: dragRef.current.originY + (e.clientY - dragRef.current.startY)
            });
        };
        const onUp = () => {
            if (dragRef.current.dragging) {
                dragRef.current.dragging = false;
            }
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
    }, []);
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [onClose]);
    const meetingIdTitle = _meetingId
        ? formatMeetingIdForTitle(_meetingId)
        : '';
    const decodedUrl = getDecodedURI(_inviteUrl);
    return (React.createElement("div", { className: classes.overlay, "data-testid": 'invite-dialog-overlay' },
        React.createElement("div", { "aria-label": t('addPeople.meetingIdTitle', { meetingId: meetingIdTitle }), "aria-modal": true, className: `${classes.panel} invite-more-dialog`, role: 'dialog', style: {
                transform: `translate(${offset.x}px, ${offset.y}px)`
            } },
            React.createElement("div", { className: classes.header, onMouseDown: onHeaderMouseDown },
                React.createElement("h1", { className: classes.title }, t('addPeople.meetingIdTitle', { meetingId: meetingIdTitle })),
                React.createElement("button", { "aria-label": t('dialog.accessibilityLabel.close'), className: classes.closeBtn, id: 'modal-header-close-button', onClick: onClose, type: 'button' },
                    React.createElement(Icon, { size: 18, src: IconCloseLarge }))),
            React.createElement("div", { className: classes.body },
                React.createElement("pre", { className: classes.inviteText }, _invitationText)),
            React.createElement("div", { className: classes.footer },
                React.createElement("button", { className: classes.copyBtn, id: 'add-people-copy-invite-button', onClick: onCopyAll, type: 'button' }, t('addPeople.copyInvite'))),
            React.createElement("span", { className: `invite-more-dialog-conference-id ${classes.srOnly}` }, t('addPeople.accessibilityLabel.meetingId', { meetingId: _meetingId })),
            React.createElement("span", { className: `invite-more-dialog-conference-url ${classes.srOnly}` }, decodedUrl))));
}
/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code AddPeopleDialog} component.
 *
 * @param {Object} state - The Redux state.
 * @param {Object} ownProps - The properties explicitly passed to the component.
 * @private
 * @returns {IProps}
 */
function mapStateToProps(state, ownProps) {
    return {
        _invitationText: getInviteText({ state,
            t: ownProps.t }),
        _inviteUrl: getInviteURL(state),
        _meetingId: getRoomName(state)
    };
}
export default translate(connect(mapStateToProps)(AddPeopleDialog));
